import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { useToast } from '@/hooks/errors/use-toast';
import { ImageLibraryClient } from './image-library/api/client';

interface UseAddPublicImageToProjectOptions {
  onSuccess?: (projectId: string | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for adding public images from image_library to projects
 * Creates a copy in workbook_images table
 */
export const useAddPublicImageToProject = (itemId: string, options: UseAddPublicImageToProjectOptions = {}) => {
  const { toast } = useToast();
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // State for tracking project assignments
  const [assignedProjects, setAssignedProjects] = useState<string[]>([]);
  const [optimisticProjectId, setOptimisticProjectId] = useState<string | null>(null);
  const [hasOptimisticUpdate, setHasOptimisticUpdate] = useState(false);

  // Check if image is already in a specific project
  const checkProjectAssignment = useCallback(async (projectId: string): Promise<boolean> => {
    if (!user?.id) return false;
    
    try {
      return await ImageLibraryClient.checkImageInProject(itemId, user.id, projectId);
    } catch (error) {
      console.warn('Failed to check project assignment:', error);
      return false;
    }
  }, [itemId, user?.id]);

  // Mutation for copying image to project
  const copyToProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // ✅ REMOVED: checkProjectAssignment call that was causing 406 errors
      // Public images can be copied multiple times - duplicates are fine
      // The UI can handle "already copied" state through local tracking if needed

      await ImageLibraryClient.copyToWorkbookImages(itemId, user.id, projectId);
      return projectId;
    },
    onMutate: (projectId) => {
      // Optimistic update
      setOptimisticProjectId(projectId);
      setHasOptimisticUpdate(true);
      setAssignedProjects(prev => [...prev, projectId]);
    },
    onSuccess: (projectId) => {
      // Update actual state with server response
      setOptimisticProjectId(null);
      setHasOptimisticUpdate(false);
      
      // Comprehensive cache invalidation to match private image patterns
      // Invalidate public image library caches
      queryClient.invalidateQueries({ queryKey: ['imageLibrary'] });
      
      // FIXED: Invalidate workbook/project data caches with exact: false for filter variations
      queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['workbook-images'] });
      
      // FIXED: Invalidate BOTH Project Image Library AND Private Image Generator caches
      // Project Image Library (project-specific images)
      queryClient.invalidateQueries({ queryKey: ['private-image-library'] }); // All projects
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count'] }); // All counts
      queryClient.invalidateQueries({ queryKey: ['private-image-library', projectId] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count', projectId] });
      
      // Private Image Generator (user's private images from all sources)
      queryClient.invalidateQueries({ queryKey: ['private-image-generator'] }); // All user private images
      queryClient.invalidateQueries({ queryKey: ['private-image-generator-count'] }); // All user private image counts
      
      // Invalidate universal storage caches (since public images can appear there)
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-library-data'] });
      
      toast({
        title: "Added to Project",
        description: "Image successfully copied to project",
        variant: "default"
      });
      
      options.onSuccess?.(projectId);
    },
    onError: (error, projectId) => {
      // Revert optimistic update
      setOptimisticProjectId(null);
      setHasOptimisticUpdate(false);
      setAssignedProjects(prev => prev.filter(id => id !== projectId));
      
      console.error('Failed to copy image to project:', error);
      toast({
        title: "Failed to Add to Project",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      
      options.onError?.(error instanceof Error ? error : new Error('Unknown error'));
    }
  });

  // Check if image is in any of the tracked projects
  const isInAnyProject = useMemo(() => {
    return assignedProjects.length > 0 || hasOptimisticUpdate;
  }, [assignedProjects.length, hasOptimisticUpdate]);

  return {
    // Actions
    copyToProject: copyToProjectMutation.mutateAsync,
    checkProjectAssignment,
    
    // State
    assignedProjects,
    isInAnyProject,
    isPending: copyToProjectMutation.isPending,
    
    // Optimistic state (for UI feedback)
    optimisticProjectId,
    hasOptimisticUpdate
  };
}; 